import React, { useRef, useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { debounce } from 'lodash'
import ReactPixel from 'react-facebook-pixel'
import { useTranslation } from 'react-i18next'

import { breakpoints } from '@src/constants/breakpoints'
import { MerchantType } from '@src/graphql-types'
import { useLockBodyScroll } from '@src/hooks/useLockBodyScroll'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { screenResolutionVar } from '@src/models/screenResolution'

import {
  getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Cuisine,
  getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Outlet,
} from './queries/__generated__/getSearchOutletsAndCuisinesMobile'
import { getSearchOutletsAndCuisinesMobileDocument } from './queries/__generated__/getSearchOutletsAndCuisinesMobile.graphql-interface'
import { SearchHead } from './SearchHead'
import { SearchHeadMobile } from './SearchHeadMobile'
import { RefContainer } from './SearchHeadStyles'
import { SearchResults } from './SearchResults'
import { isSearchFocusedVar } from '../utils/reactiveVar'

const MIN_SEARCH_TERM_LENGTH = 3

function isCuisine(
  outletOrCategory:
    | getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Cuisine
    | getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Outlet
): outletOrCategory is getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Cuisine {
  return outletOrCategory.__typename === 'Cuisine'
}

function isOutlet(
  outletOrCategory:
    | getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Cuisine
    | getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Outlet
): outletOrCategory is getSearchOutletsAndCuisinesMobile_searchOutletsAndCategoriesByName_Outlet {
  return outletOrCategory.__typename === 'Outlet'
}

// this is the main search component which provides data and wraps the input and results
export const Search: React.FC<{
  showSearchBar?: boolean
  startAnimation?: boolean
  startClosingAnimation?: () => void
  inputRef?: React.RefObject<HTMLInputElement>
}> = ({
  showSearchBar = false,
  startAnimation = false,
  startClosingAnimation,
  inputRef,
}) => {
  const marketplace = useMarketplace()
  const [sendSearchQuery, { data, error, loading }] = useLazyQuery(
    getSearchOutletsAndCuisinesMobileDocument
  )
  const { width } = useReactiveVar(screenResolutionVar)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const searchHeadRef = useRef<HTMLDivElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        if (searchTerm.length >= MIN_SEARCH_TERM_LENGTH) {
          setShowSearchResults(true)
          void sendSearchQuery({
            variables: {
              marketplaceId: marketplace.id,
              nameContains: searchTerm,
            },
          })
          ReactPixel.track('Search', {
            search_string: searchTerm,
          })
        }
      }, 500),
    [marketplace.id, sendSearchQuery, setShowSearchResults]
  )

  const categories = (data?.searchOutletsAndCategoriesByName || []).filter(
    isCuisine
  )
  const outlets = (data?.searchOutletsAndCategoriesByName || []).filter(
    isOutlet
  )

  const { t } = useTranslation('serviceNavigation')

  const searchBarPlaceholder = () => {
    if (showSearchBar)
      return marketplace.merchantType === MerchantType.RETAIL
        ? t('search_retail')
        : t('search_restaurant')
    return ''
  }

  const handleClose = () => {
    if (startClosingAnimation) {
      startClosingAnimation()
    }
  }

  // Lock body scroll only when search results are showing
  useLockBodyScroll(showSearchResults)

  // Blur input when search results close
  useEffect(() => {
    if (!showSearchResults) {
      if (inputRef?.current) {
        inputRef.current.blur()
      }
      setIsSearchFocused(false)
      // Also blur any active element (for desktop search)
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [showSearchResults, inputRef])

  // Close search if page scrolls (only when focused but results not showing)
  useEffect(() => {
    if (showSearchResults) return // Don't check scroll when results showing (body is locked)
    if (!isSearchFocused) return // Only check when search is focused

    const handleScroll = () => {
      if (window.scrollY > 100) {
        setSearchTerm('')
        setIsSearchFocused(false)
        if (inputRef?.current) {
          inputRef.current.blur()
        }
        if (startClosingAnimation) {
          startClosingAnimation()
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [showSearchResults, isSearchFocused, inputRef, startClosingAnimation])

  // Sync search focus state with reactive variable for SiteHeader
  useEffect(() => {
    isSearchFocusedVar(isSearchFocused)
  }, [isSearchFocused])

  return (
    <>
      {width < breakpoints.tabletMisc ? (
        <SearchHeadMobile
          executeSearch={debouncedSearch}
          showSearchBar={showSearchBar}
          startAnimation={startAnimation}
          placeholder={searchBarPlaceholder()}
          startClosingAnimation={handleClose}
          closeSearchResults={() => setShowSearchResults(false)}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showCategories
          isSearchFocused={isSearchFocused}
          setIsSearchFocused={setIsSearchFocused}
          inputRef={inputRef}
        />
      ) : (
        <RefContainer ref={searchHeadRef}>
          <SearchHead
            executeSearch={debouncedSearch}
            closeSearchResults={() => setShowSearchResults(false)}
            categories={categories}
            outlets={outlets}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </RefContainer>
      )}

      {showSearchResults && (
        <SearchResults
          categories={categories}
          outlets={outlets}
          error={!!error}
          loading={loading}
          closeSearchResults={() => setShowSearchResults(false)}
          setSearchTerm={setSearchTerm}
          containerWidth={`${searchHeadRef.current?.offsetWidth}px`}
          startClosingAnimation={startClosingAnimation}
        />
      )}
    </>
  )
}
